// Saved filters ("filters:<page>", "sidebarClaimFilter") are stored per tenant and user,
// encrypted with AES-GCM under a key the backend sends with the session
// (user.backend.filters_encryption_key, base64, with its non-secret id filters_key_id). The
// key is only ever held in memory. All of them live in one fixed localStorage entry:
//   "filters" -> {"v":2,"kid":"<key id>","iv":"<base64>","ct":"<base64>"}
// whose plaintext is { "<tenantId>:<userId>": { "filters:claims": …, … } }, so neither the
// tenant, the user nor the page names are readable outside the ciphertext. The fixed string
// plus kid is bound as AES-GCM additional data. A blob under another kid (the key was rotated,
// e.g. by an explicit logout) or one that fails to decrypt is unrecoverable: it is dropped and
// the next write starts a new one, so nothing is ever left behind to sweep.
//
// WebCrypto is async but the per-page helpers are sync: unlockTenantFilters decrypts the
// scope's filters once into memory, reads are served from there, and writes update memory at
// once and are encrypted to localStorage in the background, in order. As every page (and tab)
// shares the one entry, a write re-reads it and merges only the changed pages of its own scope,
// under a Web Lock across tabs, instead of writing back an in-memory snapshot.
// The scope ("<tenantId>:<userId>") comes from MainStore.filtersScope. Until it and the key
// are known (before the session loads, or when the backend sends no key) reads return
// nothing and writes are dropped.
// The sidebar chat-history panel's "sidebarFilters" (ChatStore) stays global and in plain
// text on purpose: it only holds view preferences (sort, chat types, last active, hide
// closed), no tenant data.

/** The localStorage entry holding every scope's saved filters (one encrypted blob). */
export const FILTERS_STORAGE_KEY = "filters";

const FORMAT_VERSION = 2;
const IV_BYTES = 12;
const MIN_KEY_BYTES = 32;
const ADDITIONAL_DATA_PREFIX = "promptwrx:saved-filters:v2:";
const LOCK_NAME = "promptwrx:saved-filters";

// The page keys. Also the keys used before filters were stored per tenant; those, the
// plain-text per-tenant "<baseKey>:<tenantId>:<userId>" ones that came after, and the
// per-page "<baseKey>:enc:<hmac>" blobs before the single entry, can't be migrated (the first
// are readable by anyone, the last under a key name nothing derives any more), so they're
// deleted once the scope is known. Listed literally: the per-page helpers import this module.
const FILTERS_BASE_KEYS = [
  "filters:claims",
  "filters:dashboard",
  "filters:observability",
  "filters:policies",
  "filters:contracts",
  "sidebarClaimFilter",
];

/** scope -> baseKey -> saved value: the plaintext of the blob. */
type SavedFilters = Record<string, Record<string, unknown>>;

interface UnlockedState {
  scope: string;
  keyId: string;
  aesKey: CryptoKey;
  /** baseKey -> JSON of the saved value; the source of truth while unlocked. */
  values: Map<string, string>;
  /** baseKeys whose value changed since it was last encrypted to localStorage. */
  dirty: Set<string>;
  /** Set by a wipe: writes still queued or in flight must not land. */
  discarded: boolean;
}

let unlocked: UnlockedState | null = null;
// Bumped on every unlock/lock, so an unlock that finishes after a newer one is dropped.
let generation = 0;
let pendingWrites: Promise<void> = Promise.resolve();

const encoder = new TextEncoder();
const decoder = new TextDecoder();

const bytesToBase64 = (bytes: Uint8Array): string => {
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary);
};

// Accepts standard and URL-safe base64, with or without padding.
// Built with `new Uint8Array(length)` so it types as backed by an ArrayBuffer (a WebCrypto BufferSource).
const base64ToBytes = (value: string) => {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(normalized + "=".repeat((4 - (normalized.length % 4)) % 4));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const deriveKey = async (keyBase64: string): Promise<CryptoKey> => {
  const raw = base64ToBytes(keyBase64);
  if (raw.length < MIN_KEY_BYTES) throw new Error("filters encryption key too short");
  const master = await crypto.subtle.importKey("raw", raw, "HKDF", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt: new Uint8Array(0), info: encoder.encode("filters:aes-gcm") },
    master,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
};

const additionalData = (keyId: string) => encoder.encode(ADDITIONAL_DATA_PREFIX + keyId);

const encrypt = async (aesKey: CryptoKey, keyId: string, all: SavedFilters): Promise<string> => {
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv, additionalData: additionalData(keyId) },
    aesKey,
    encoder.encode(JSON.stringify(all)),
  );
  return JSON.stringify({ v: FORMAT_VERSION, kid: keyId, iv: bytesToBase64(iv), ct: bytesToBase64(new Uint8Array(ct)) });
};

/** Every scope's saved filters from the blob; {} when there is none. A blob it can't read
 * (another kid, corrupt, tampered with) is dropped, as it is unrecoverable. */
const readStored = async (aesKey: CryptoKey, keyId: string): Promise<SavedFilters> => {
  const stored = localStorage.getItem(FILTERS_STORAGE_KEY);
  if (!stored) return {};
  try {
    const blob = JSON.parse(stored);
    if (blob?.v !== FORMAT_VERSION || blob.kid !== keyId || typeof blob.iv !== "string" || typeof blob.ct !== "string") {
      throw new Error("filters blob not readable with this key");
    }
    const plain = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: base64ToBytes(blob.iv), additionalData: additionalData(keyId) },
      aesKey,
      base64ToBytes(blob.ct),
    );
    const all = JSON.parse(decoder.decode(plain));
    if (!isPlainObject(all) || !Object.values(all).every(isPlainObject)) throw new Error("unknown filters format");
    return all as SavedFilters;
  } catch {
    safeRemove(FILTERS_STORAGE_KEY);
    return {};
  }
};

// Serializes read-merge-write of the blob across tabs. Without Web Locks (old browsers, tests)
// it still re-reads before writing, which only leaves a narrow window for a lost update.
const withStorageLock = <T>(fn: () => Promise<T>): Promise<T> => {
  const locks = globalThis.navigator?.locks;
  return locks ? locks.request(LOCK_NAME, fn) : fn();
};

const localStorageKeys = (): string[] => {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key) keys.push(key);
  }
  return keys;
};

const safeRemove = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore localStorage errors
  }
};

/** Deletes the saved filters of the formats before the single "filters" entry: the global
 * pre-tenant keys, the plain-text "<baseKey>:<tenantId>:<userId>" ones and the per-page
 * "<baseKey>:enc:<hmac>" blobs. */
export const removeLegacyFilters = (): void => {
  try {
    localStorageKeys().forEach((key) => {
      if (FILTERS_BASE_KEYS.some((base) => key === base || key.startsWith(`${base}:`))) localStorage.removeItem(key);
    });
  } catch {
    // Ignore localStorage errors
  }
};

/** Unlocks the saved filters of `scope` with the session's key (and its id): decrypts them into
 * memory. Without a scope or a usable key the storage stays locked (reads return nothing, writes
 * are dropped). Resolves to whether it is unlocked, false also when a newer call superseded it. */
export const unlockTenantFilters = async (
  scope: string | null,
  keyBase64: string | null | undefined,
  keyId: string | null | undefined,
): Promise<boolean> => {
  const current = ++generation;
  unlocked = null;
  if (!scope || !keyBase64 || !keyId || !globalThis.crypto?.subtle) return false;

  try {
    const aesKey = await deriveKey(keyBase64);
    // Let writes still queued from the previous unlock land, so they are read back below.
    await pendingWrites;
    if (current !== generation) return false;

    const all = await withStorageLock(() => readStored(aesKey, keyId));
    if (current !== generation) return false;

    const values = new Map(Object.entries(all[scope] ?? {}).map(([baseKey, value]) => [baseKey, JSON.stringify(value)]));
    unlocked = { scope, keyId, aesKey, values, dirty: new Set(), discarded: false };
    return true;
  } catch {
    return false;
  }
};

/** Drops the in-memory key and filters (e.g. once the session is gone). */
export const lockTenantFilters = (): void => {
  generation++;
  unlocked = null;
};

/** Deletes the saved filters entry from localStorage and locks, dropping the in-memory key.
 * For an explicit sign-out: a page refresh keeps them. Writes still queued are discarded, not
 * written back. */
export const wipeTenantFilters = async (): Promise<void> => {
  const state = unlocked;
  lockTenantFilters();
  if (state) {
    state.discarded = true;
    state.dirty.clear();
  }
  await pendingWrites;
  await withStorageLock(async () => safeRemove(FILTERS_STORAGE_KEY));
};

/** Resolves once every queued write has been encrypted to localStorage. */
export const flushTenantFilters = (): Promise<void> => pendingWrites;

const flushDirty = async (state: UnlockedState): Promise<void> => {
  const baseKeys = Array.from(state.dirty);
  if (baseKeys.length === 0) return;
  state.dirty.clear();
  try {
    await withStorageLock(async () => {
      if (state.discarded) return;
      // Re-read: other tabs may have saved other pages (or scopes) since this one unlocked.
      const all = await readStored(state.aesKey, state.keyId);
      const own = { ...all[state.scope] };
      baseKeys.forEach((baseKey) => {
        const json = state.values.get(baseKey);
        if (json === undefined) delete own[baseKey];
        else own[baseKey] = JSON.parse(json);
      });
      if (Object.keys(own).length > 0) all[state.scope] = own;
      else delete all[state.scope];

      const stored = Object.keys(all).length > 0 ? await encrypt(state.aesKey, state.keyId, all) : null;
      if (state.discarded) return;
      try {
        if (stored) localStorage.setItem(FILTERS_STORAGE_KEY, stored);
        else localStorage.removeItem(FILTERS_STORAGE_KEY);
      } catch {
        // Ignore localStorage errors
      }
    });
  } catch {
    // Not saved: keep them dirty, so the next write retries them.
    if (!state.discarded) baseKeys.forEach((baseKey) => state.dirty.add(baseKey));
  }
};

const schedulePersist = (state: UnlockedState, baseKey: string) => {
  state.dirty.add(baseKey);
  pendingWrites = pendingWrites.then(() => flushDirty(state)).catch(() => undefined);
};

const unlockedFor = (scope: string | null): UnlockedState | null =>
  scope && unlocked?.scope === scope ? unlocked : null;

export const readTenantFilters = (baseKey: string, scope: string | null): unknown => {
  const json = unlockedFor(scope)?.values.get(baseKey);
  if (json === undefined) return null;
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
};

export const writeTenantFilters = (baseKey: string, scope: string | null, value: unknown): void => {
  const state = unlockedFor(scope);
  if (!state) return;
  const json = JSON.stringify(value);
  if (json === undefined) return;
  state.values.set(baseKey, json);
  schedulePersist(state, baseKey);
};

export const removeTenantFilters = (baseKey: string, scope: string | null): void => {
  const state = unlockedFor(scope);
  if (!state) return;
  state.values.delete(baseKey);
  schedulePersist(state, baseKey);
};
